import { persist } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

type BetaAnnouncementState = {
	/** Dialog visibility. Ephemeral — never persisted. */
	isOpen: boolean
	/**
	 * Whether the user explicitly clicked "Got it". Persisted to localStorage so
	 * the attention glow stays dismissed across reloads. Per-device by design:
	 * the beta flag is tenant-level, but "I've seen this" is a personal client
	 * preference, so server-side per-user tracking would be overkill here.
	 */
	acknowledged: boolean
}

type BetaAnnouncementActions = {
	/** Open the dialog. Does NOT acknowledge — the glow keeps pulsing. */
	open: () => void
	/** Close the dialog without acknowledging (X / overlay / Esc). */
	close: () => void
	/** Explicit "Got it": stop the glow for good (persisted) and close. */
	acknowledge: () => void
}

type BetaAnnouncementStore = BetaAnnouncementState & {
	actions: BetaAnnouncementActions
}

const STORAGE_KEY = '@bitlyft/beta-announcement'

/**
 * Backs the global beta-announcement dialog. Opened from the sidebar button or
 * either inline glow trigger; only `acknowledge` (the dialog's "Got it") stops
 * the glow, and only that flag is persisted.
 */
export const useBetaAnnouncementStore =
	createWithEqualityFn<BetaAnnouncementStore>()(
		persist(
			(set) => ({
				isOpen: false,
				acknowledged: false,
				actions: {
					open: () => set({ isOpen: true }),
					close: () => set({ isOpen: false }),
					acknowledge: () => set({ isOpen: false, acknowledged: true }),
				},
			}),
			{
				name: STORAGE_KEY,
				// Persist only the dismissal flag; `isOpen` and `actions` stay in memory.
				partialize: (state) => ({ acknowledged: state.acknowledged }),
			},
		),
		shallow,
	)

/** Whether the announcement dialog is currently open. */
export const useBetaIsOpen = () => useBetaAnnouncementStore((s) => s.isOpen)

/**
 * Whether the user has dismissed the attention glow via "Got it" (persisted).
 * Gate the `beta-ripple` pulse on `!acknowledged` so it stops for good.
 */
export const useBetaAcknowledged = () =>
	useBetaAnnouncementStore((s) => s.acknowledged)

/** The dialog's actions (open/close/acknowledge) — stable, safe to read once. */
export const useBetaAnnouncementActions = () =>
	useBetaAnnouncementStore((s) => s.actions)
